import { ApiService } from "@core/api/api-service";
import { Gender } from "@domain/patient/gender";
import { PatientStatus } from "@domain/patient/patient";
import {
	AllPatientPrograms,
	DatedPatientProgram,
	DraftProgram,
	EndedProgram,
	ExtendedPatientProgram,
	OngoingProgram,
	PatientProgram,
	PatientProgramBase,
	PatientProgramEditionMetadata,
	ScheduledProgram,
} from "@domain/program/patient-program";
// ProgramId import removed - now using string type instead
import { ProgramStatus } from "@domain/program/program-status/program-status";
import { dateFromDateDto } from "@utils/date-utils";

export class PatientProgramsService {
	constructor(private readonly apiService: ApiService) {}

	async loadAllPatientsPrograms(): Promise<ExtendedPatientProgram[]> {
		const result = await this.apiService.get<ExtendedPatientProgramDto[]>("/programs");
		return PatientProgramsService.allExtendedPatientsProgramsFromDto(result.data);
	}

	async loadAllPatientPrograms(patientId: string): Promise<AllPatientPrograms> {
		const result = await this.apiService.get<AllPatientProgramsDto>(`/patients/${patientId}/programs`);
		return PatientProgramsService.allPatientProgramsFromDto(result.data);
	}

	async duplicateProgramAsDraft(patientId: string, programId: string): Promise<DraftProgram> {
		const result = await this.apiService.post<DraftProgramDto>(
			`/patients/${patientId}/programs/${programId}/duplicate`,
		);
		return PatientProgramsService.draftPatientProgramFromDto(result.data);
	}

	// Dto Conversions
	private static startedPatientProgramFromDto(programDto: StartedPatientProgramDto): DatedPatientProgram {
		return {
			...programDto,
			lastEditionMetadata: PatientProgramsService.lastEditionMetadataFromDto(programDto.lastEditionMetadata),
			startDate: dateFromDateDto(programDto.startDate),
			endDate: dateFromDateDto(programDto.endDate),
		};
	}

	private static ongoingPatientProgramFromDto(ongoingProgramDto: OngoingProgramDto): OngoingProgram {
		return {
			...PatientProgramsService.startedPatientProgramFromDto(ongoingProgramDto),
			status: ProgramStatus.Ongoing,
		};
	}

	private static scheduledPatientProgramFromDto(scheduledProgramDto: ScheduledProgramDto): ScheduledProgram {
		return {
			...PatientProgramsService.startedPatientProgramFromDto(scheduledProgramDto),
			status: ProgramStatus.Scheduled,
		};
	}

	private static endedPatientProgramFromDto(endedProgramDto: EndedProgramDto): EndedProgram {
		return {
			...PatientProgramsService.startedPatientProgramFromDto(endedProgramDto),
			status: ProgramStatus.Ended,
			stopped: endedProgramDto.stopped,
		};
	}

	private static draftPatientProgramFromDto(draftProgramDto: DraftProgramDto): DraftProgram {
		return {
			...draftProgramDto,
			lastEditionMetadata: PatientProgramsService.lastEditionMetadataFromDto(draftProgramDto.lastEditionMetadata),
		};
	}

	private static lastEditionMetadataFromDto(
		lastEditionMetadata: PatientProgramEditionMetadataDto,
	): PatientProgramEditionMetadata {
		return {
			...lastEditionMetadata,
			date: dateFromDateDto(lastEditionMetadata.date),
		};
	}

	private static allPatientProgramsFromDto(allPatientProgramsDto: AllPatientProgramsDto): AllPatientPrograms {
		const programsByStatus: AllPatientPrograms = allPatientProgramsDto.reduce(
			(acc, program) => {
				switch (program.status) {
					case ProgramStatus.Ongoing:
						return { ...acc, ongoingProgram: PatientProgramsService.ongoingPatientProgramFromDto(program) };
					case ProgramStatus.Draft:
						return {
							...acc,
							draftPrograms: [...acc.draftPrograms, PatientProgramsService.draftPatientProgramFromDto(program)],
						};
					case ProgramStatus.Scheduled:
						return {
							...acc,
							scheduledPrograms: [
								...acc.scheduledPrograms,
								PatientProgramsService.scheduledPatientProgramFromDto(program),
							],
						};
					case ProgramStatus.Ended:
						return {
							...acc,
							endedPrograms: [...acc.endedPrograms, PatientProgramsService.endedPatientProgramFromDto(program)],
						};
				}
			},
			{
				scheduledPrograms: [],
				draftPrograms: [],
				endedPrograms: [],
			} as AllPatientPrograms,
		);
		const sortedDraftPrograms = [...programsByStatus.draftPrograms].sort((a, b) =>
			b.lastEditionMetadata.date.diff(a.lastEditionMetadata.date),
		);
		const sortedScheduledPrograms = [...programsByStatus.scheduledPrograms].sort((a, b) =>
			a.startDate.diff(b.startDate),
		);
		const sortedEndedPrograms = [...programsByStatus.endedPrograms].sort((a, b) => b.endDate.diff(a.endDate));
		return {
			ongoingProgram: programsByStatus.ongoingProgram,
			draftPrograms: sortedDraftPrograms,
			scheduledPrograms: sortedScheduledPrograms,
			endedPrograms: sortedEndedPrograms,
		};
	}

	private static readonly getPatientProgramFromDto = (dto: PatientProgramDto): PatientProgram => {
		switch (dto.status) {
			case ProgramStatus.Ongoing:
				return PatientProgramsService.ongoingPatientProgramFromDto(dto);
			case ProgramStatus.Scheduled:
				return PatientProgramsService.scheduledPatientProgramFromDto(dto);
			case ProgramStatus.Draft:
				return PatientProgramsService.draftPatientProgramFromDto(dto);
			case ProgramStatus.Ended:
				return PatientProgramsService.endedPatientProgramFromDto(dto);
		}
	};

	private static readonly allExtendedPatientsProgramsFromDto = (
		allExtendedPatientProgramsDto: ExtendedPatientProgramDto[],
	): ExtendedPatientProgram[] =>
		allExtendedPatientProgramsDto.map(dto => {
			const patientProgram = PatientProgramsService.getPatientProgramFromDto(dto);
			return {
				...patientProgram,
				patient: dto.patient,
			} as ExtendedPatientProgram;
		});
}

interface PatientProgramBaseDto extends Omit<PatientProgramBase, "lastEditionMetadata"> {
	lastEditionMetadata: PatientProgramEditionMetadataDto;
}

interface PatientProgramEditionMetadataDto extends Omit<PatientProgramEditionMetadata, "date"> {
	date: string;
}

interface StartedPatientProgramDto extends PatientProgramBaseDto {
	startDate: string;
	endDate: string;
}

type PatientProgramDto = OngoingProgramDto | ScheduledProgramDto | DraftProgramDto | EndedProgramDto;
type AllPatientProgramsDto = PatientProgramDto[];

interface OngoingProgramDto extends StartedPatientProgramDto {
	status: ProgramStatus.Ongoing;
}

interface ScheduledProgramDto extends StartedPatientProgramDto {
	status: ProgramStatus.Scheduled;
}

interface DraftProgramDto extends PatientProgramBaseDto {
	status: ProgramStatus.Draft;
}

interface EndedProgramDto extends StartedPatientProgramDto {
	status: ProgramStatus.Ended;
	stopped: boolean;
}

interface PatientProgramProfileDto {
	id: string;
	analyticsId: string;
	firstName: string;
	lastName: string;
	gender?: Gender;
	status: PatientStatus;
}

type ExtendedPatientProgramDto = PatientProgramDto & {
	patient: PatientProgramProfileDto;
};
